import React, { useState, useRef, useEffect, useCallback } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { FiChevronLeft, FiChevronRight, FiCheckCircle, FiLayers, FiTrendingUp, FiSettings, FiShield, FiArrowLeft, FiArrowRight } from "react-icons/fi";
import { FaGift, FaTruckMoving, FaHospitalAlt, FaPlaneDeparture, FaSchool } from "react-icons/fa";
import { MdOutlineWorkspaces, MdOutlineHealthAndSafety } from "react-icons/md";
import { HiOutlineBuildingOffice2, HiOutlineTrophy } from "react-icons/hi2";
import { RiHotelLine } from "react-icons/ri";
import SectionHeader from "../SectionHeader";
import { useTranslation } from "react-i18next";

// Import translations
import fa from "./locales/BusinessSolutions-fa.json";
import en from "./locales/BusinessSolutions-en.json";
import ar from "./locales/BusinessSolutions-ar.json";
import tr from "./locales/BusinessSolutions-tr.json";

const useResponsive = () => {
    const [screenSize, setScreenSize] = useState({
        width: typeof window !== "undefined" ? window.innerWidth : 0,
        height: typeof window !== "undefined" ? window.innerHeight : 0,
        isMobile: false,
        isTablet: false,
        isDesktop: false,
    });

    const updateScreenSize = useCallback(() => {
        const width = window.innerWidth;
        setScreenSize({
            width,
            height: window.innerHeight,
            isMobile: width < 640,
            isTablet: width >= 640 && width < 1024,
            isDesktop: width >= 1024,
        });
    }, []);

    useEffect(() => {
        if (typeof window === "undefined") return;
        updateScreenSize();
        window.addEventListener("resize", updateScreenSize);
        return () => window.removeEventListener("resize", updateScreenSize);
    }, [updateScreenSize]);

    return screenSize;
};

const BusinessSolutions: React.FC = () => {
    const { t, i18n } = useTranslation();

    useEffect(() => {
        const ns = "BusinessSolutions";
        const resources = { fa, en, ar, tr } as const;
        Object.entries(resources).forEach(([lng, data]) => {
            if (!i18n.hasResourceBundle(lng, ns)) {
                i18n.addResourceBundle(lng, ns, data, true, true);
            }
        });
    }, [i18n]);

    const [currentSlide, setCurrentSlide] = useState(0);
    const [selectedCard, setSelectedCard] = useState<number | null>(null);
    const [showDetails, setShowDetails] = useState(false);
    const screenSize = useResponsive();
    const sliderRef = useRef<HTMLDivElement>(null);

    const currentLang = i18n.language;
    const isRTL = ["fa", "ar"].includes(currentLang);
    const dir = isRTL ? "rtl" : "ltr";

    const getMaxVisibleCards = useCallback(() => {
        if (screenSize.isMobile) return 1;
        if (screenSize.isTablet) return 2;
        return 4;
    }, [screenSize]);

    const maxVisibleCards = getMaxVisibleCards();
    const lastScreenSize = useRef(screenSize.width);

    const scrollToContact = () => {
        setShowDetails(false);
        setTimeout(() => {
            const contactSection = document.getElementById("contact-section");
            if (contactSection) {
                contactSection.scrollIntoView({ behavior: "smooth", block: "start" });
            }
        }, 300);
    };

    useEffect(() => {
        if (lastScreenSize.current !== screenSize.width) {
            setCurrentSlide(0);
            lastScreenSize.current = screenSize.width;
        }
    }, [screenSize.width]);

    const icons = [
        <FaGift className="w-5 h-5" key="ic0" />,
        <MdOutlineWorkspaces className="w-5 h-5" key="ic1" />,
        <FaSchool className="w-5 h-5" key="ic2" />,
        <FaHospitalAlt className="w-5 h-5" key="ic3" />,
        <FaTruckMoving className="w-5 h-5" key="ic4" />,
        <MdOutlineHealthAndSafety className="w-5 h-5" key="ic5" />,
        <HiOutlineBuildingOffice2 className="w-5 h-5" key="ic6" />,
        <RiHotelLine className="w-5 h-5" key="ic7" />,
        <HiOutlineTrophy className="w-5 h-5" key="ic8" />,
        <FaPlaneDeparture className="w-5 h-5" key="ic9" />,
    ];

    const services = Array.from({ length: 10 }, (_, i) => {
        const srv = t(`services.${i}`, { ns: "BusinessSolutions", returnObjects: true, defaultValue: null }) as any;
        if (srv && srv.title) {
            return {
                ...srv,
                icon: icons[i],
            };
        }
        return null;
    }).filter(Boolean) as Array<{
        title: string;
        summary: string;
        details: string;
        category: string;
        icon: React.ReactNode;
    }>;

    const totalSlides = Math.ceil(services.length / maxVisibleCards);

    const nextSlide = () => {
        setCurrentSlide((prev) => (prev - 1 + totalSlides) % totalSlides);
    };

    const prevSlide = () => {
        setCurrentSlide((prev) => (prev + 1) % totalSlides);
    };

    const handleCardClick = (index: number) => {
        setSelectedCard(index);
        setShowDetails(true);
    };

    const closeDetails = () => {
        setShowDetails(false);
    };

    const getVisibleCards = () => {
        const startIndex = currentSlide * maxVisibleCards;
        return services.slice(startIndex, startIndex + maxVisibleCards);
    };

    useEffect(() => {
        if (!showDetails) {
            const interval = setInterval(() => {
                nextSlide();
            }, 14000);
            return () => clearInterval(interval);
        }
    }, [showDetails, currentSlide]);

    return (
        <section dir={dir} id="business-solutions" className="pb-32 overflow-hidden">
            <div className="container mx-auto px-4 sm:px-10">
                <div className="flex flex-col items-center text-center mb-6">
                    <div className="relative">
                        <SectionHeader
                            regularText={t("sectionHeader.regularText", { ns: "BusinessSolutions" })}
                            highlightedText={t("sectionHeader.highlightedText", { ns: "BusinessSolutions" })}
                            descriptionText={t("sectionHeader.descriptionText", { ns: "BusinessSolutions" })}
                            descriptionHighlight={t("sectionHeader.descriptionHighlight", { ns: "BusinessSolutions" })}
                        />
                    </div>
                </div>

                <div className="relative">
                    <div className="overflow-hidden" ref={sliderRef}>
                        <AnimatePresence mode="wait">
                            <div
                                key={`${currentSlide}-${maxVisibleCards}`}
                                className={`grid grid-cols-1 ${maxVisibleCards >= 2 ? "sm:grid-cols-2" : ""} ${maxVisibleCards >= 4 ? "lg:grid-cols-4" : ""} gap-6 py-4`}
                            >
                                {getVisibleCards().map((service, index) => {
                                    const actualIndex = currentSlide * maxVisibleCards + index;
                                    return (
                                        <motion.div
                                            key={actualIndex}
                                            className="relative overflow-hidden rounded-2xl bg-white/90 backdrop-blur-md border border-slate-200/80 hover:border-blue-300 shadow-lg shadow-slate-200/50 hover:shadow-xl hover:shadow-blue-500/10 transition-all duration-300 p-6 flex flex-col justify-between cursor-pointer group min-h-[280px]"
                                            whileHover={{ y: -4 }}
                                            transition={{ duration: 0.25 }}
                                            onClick={() => handleCardClick(actualIndex)}
                                            layoutId={`card-${actualIndex}`}
                                            initial={{ opacity: 0, y: 20 }}
                                            animate={{ opacity: 1, y: 0 }}
                                            exit={{ opacity: 0, y: -20 }}
                                        >
                                            <div>
                                                <div className="flex items-center justify-between mb-5">
                                                    <div className="w-11 h-11 rounded-xl bg-gradient-to-br from-[#0038A8] via-[#0072CE] to-[#155DFC] flex items-center justify-center text-white text-xl shadow-md shadow-blue-500/20">
                                                        {service.icon}
                                                    </div>
                                                    <span className="text-xs font-semibold text-slate-600 bg-slate-100/90 px-3 py-1 rounded-full border border-slate-200/60">
                                                        {service.category}
                                                    </span>
                                                </div>

                                                <h3 className="text-lg font-bold text-slate-800 mb-2 group-hover:text-[#0072CE] transition-colors">
                                                    {service.title}
                                                </h3>

                                                <p className="text-slate-600 text-sm leading-relaxed line-clamp-3">
                                                    {service.summary}
                                                </p>
                                            </div>

                                            <div className="mt-6 pt-4 border-t border-slate-100 flex items-center justify-between text-[#0072CE] text-xs font-semibold">
                                                <span>{t("labels.requestConsultation", { ns: "BusinessSolutions" })}</span>
                                                <span className="text-base group-hover:translate-x-[-3px] rtl:group-hover:translate-x-[3px] transition-transform">←</span>
                                            </div>
                                        </motion.div>
                                    );
                                })}
                            </div>
                        </AnimatePresence>
                    </div>

                    {totalSlides > 1 && (
                        <>
                            <button
                                onClick={prevSlide}
                                className="absolute left-2 sm:left-4 top-1/2 -translate-y-1/2 z-20 w-10 h-10 rounded-full bg-white/90 backdrop-blur-md flex items-center justify-center text-slate-700 hover:bg-white hover:text-[#0072CE] transition-all border border-slate-200 shadow-md"
                                aria-label={t("labels.previousSlide", { ns: "BusinessSolutions" })}
                            >
                                <FiChevronLeft className="w-5 h-5" />
                            </button>

                            <button
                                onClick={nextSlide}
                                className="absolute right-2 sm:right-4 top-1/2 -translate-y-1/2 z-20 w-10 h-10 rounded-full bg-white/90 backdrop-blur-md flex items-center justify-center text-slate-700 hover:bg-white hover:text-[#0072CE] transition-all border border-slate-200 shadow-md"
                                aria-label={t("labels.nextSlide", { ns: "BusinessSolutions" })}
                            >
                                <FiChevronRight className="w-5 h-5" />
                            </button>
                        </>
                    )}
                </div>

                {totalSlides > 1 && (
                    <div className="flex justify-center mt-6 gap-1">
                        {Array.from({ length: totalSlides }).map((_, index) => (
                            <button
                                key={index}
                                onClick={() => setCurrentSlide(index)}
                                className="p-3 flex items-center justify-center cursor-pointer touch-manipulation"
                                aria-label={`${t("labels.goToSlide", { ns: "BusinessSolutions" })} ${index + 1}`}
                            >
                                <span
                                    className={`h-2.5 rounded-full transition-all duration-300 block ${
                                        index === currentSlide ? "w-8 bg-[#0072CE]" : "w-2.5 bg-slate-300 hover:bg-slate-400"
                                    }`}
                                />
                            </button>
                        ))}
                    </div>
                )}

                <AnimatePresence>
                    {showDetails && selectedCard !== null && (
                        <motion.div
                            initial={{ opacity: 0 }}
                            animate={{ opacity: 1 }}
                            exit={{ opacity: 0 }}
                            className="fixed inset-0 z-[100] flex items-center justify-center p-4 sm:p-6 bg-slate-900/60 backdrop-blur-md overflow-y-auto"
                            onClick={closeDetails}
                        >
                            <motion.div
                                layoutId={`card-${selectedCard}`}
                                className="relative max-w-2xl w-full max-h-[88vh] bg-white backdrop-blur-2xl p-6 sm:p-8 rounded-3xl shadow-2xl border border-slate-100 overflow-y-auto my-auto scrollbar-thin scrollbar-thumb-slate-200"
                                onClick={(e) => e.stopPropagation()}
                            >
                                {/* Top Gradient Accent Line */}
                                <div className="h-1.5 w-full bg-gradient-to-r from-[#0038A8] via-[#0072CE] to-[#155DFC] absolute top-0 start-0" />

                                <button
                                    onClick={closeDetails}
                                    className="absolute top-5 end-5 w-8 h-8 rounded-full bg-slate-100 hover:bg-slate-200 text-slate-500 hover:text-slate-800 flex items-center justify-center text-lg transition-colors font-semibold z-20"
                                    aria-label={t("labels.close", { ns: "BusinessSolutions" })}
                                >
                                    ×
                                </button>

                                <div className="pt-1">
                                    {/* Header Info */}
                                    <div className="flex items-center justify-between mb-4">
                                        <div className="flex items-center gap-3">
                                            <div className="w-12 h-12 rounded-2xl bg-gradient-to-br from-[#0038A8] via-[#0072CE] to-[#155DFC] flex items-center justify-center text-white text-xl shadow-md shadow-blue-500/20 shrink-0">
                                                {services[selectedCard].icon}
                                            </div>
                                            <div>
                                                <span className="text-xs font-semibold text-[#0072CE] bg-blue-50 border border-blue-100/80 px-3 py-1 rounded-full">
                                                    {services[selectedCard].category}
                                                </span>
                                            </div>
                                        </div>
                                    </div>

                                    <h3 className="text-xl sm:text-2xl font-bold text-slate-800 mb-3">
                                        {services[selectedCard].title}
                                    </h3>

                                    {/* Section 1: Executive Overview */}
                                    <div className="bg-gradient-to-br from-blue-50/70 to-slate-50 p-4 sm:p-5 rounded-2xl border border-blue-100/80 mb-6">
                                        <div className="flex items-center gap-2 text-[#0072CE] font-bold text-xs uppercase tracking-wider mb-2">
                                            <FiLayers className="w-4 h-4" />
                                            <span>{isRTL ? "معرفی و کلیات راهکار" : "Solution Overview"}</span>
                                        </div>
                                        <p className="text-slate-800 text-sm font-semibold mb-2 leading-relaxed">
                                            {services[selectedCard].summary}
                                        </p>
                                        <p className="text-slate-600 text-xs sm:text-sm leading-relaxed">
                                            {services[selectedCard].details}
                                        </p>
                                    </div>

                                    {/* Section 2: Key Capabilities Grid */}
                                    <div className="mb-6">
                                        <h4 className="text-sm font-bold text-slate-800 mb-3 flex items-center gap-2">
                                            <FiCheckCircle className="w-4 h-4 text-[#0072CE]" />
                                            <span>{isRTL ? "قابلیت‌ها و ویژگی‌های کلیدی" : "Key Capabilities"}</span>
                                        </h4>
                                        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                                            <div className="flex items-start gap-2.5 p-3 rounded-xl bg-slate-50 border border-slate-100 text-xs text-slate-700">
                                                <FiCheckCircle className="w-4 h-4 text-[#0072CE] shrink-0 mt-0.5" />
                                                <span>{isRTL ? "انطباق کامل با استانداردها و ساختار سازمان" : "Full compliance with organizational standards"}</span>
                                            </div>
                                            <div className="flex items-start gap-2.5 p-3 rounded-xl bg-slate-50 border border-slate-100 text-xs text-slate-700">
                                                <FiCheckCircle className="w-4 h-4 text-[#0072CE] shrink-0 mt-0.5" />
                                                <span>{isRTL ? "قابلیت سفارشی‌سازی و توسعه اختصاصی" : "Customizable features & dedicated workflow"}</span>
                                            </div>
                                            <div className="flex items-start gap-2.5 p-3 rounded-xl bg-slate-50 border border-slate-100 text-xs text-slate-700">
                                                <FiCheckCircle className="w-4 h-4 text-[#0072CE] shrink-0 mt-0.5" />
                                                <span>{isRTL ? "یکپارچه‌سازی با زیرساخت‌ها و سامانه‌های موجود" : "Seamless integration with existing infrastructure"}</span>
                                            </div>
                                            <div className="flex items-start gap-2.5 p-3 rounded-xl bg-slate-50 border border-slate-100 text-xs text-slate-700">
                                                <FiCheckCircle className="w-4 h-4 text-[#0072CE] shrink-0 mt-0.5" />
                                                <span>{isRTL ? "داشبورد نظارت و گزارش‌دهی مدیریت" : "Executive monitoring & reporting dashboard"}</span>
                                            </div>
                                        </div>
                                    </div>

                                    {/* Section 3: Implementation Workflow */}
                                    <div className="mb-6">
                                        <h4 className="text-sm font-bold text-slate-800 mb-3 flex items-center gap-2">
                                            <FiSettings className="w-4 h-4 text-[#0072CE]" />
                                            <span>{isRTL ? "مراحل اجرای راهکار" : "Implementation Workflow"}</span>
                                        </h4>
                                        <div className="grid grid-cols-3 gap-2 sm:gap-3 text-center">
                                            <div className="p-3 rounded-xl bg-blue-50/50 border border-blue-100/60">
                                                <span className="inline-block w-6 h-6 rounded-full bg-[#0072CE] text-white text-xs font-bold leading-6 mb-1">1</span>
                                                <h5 className="text-xs font-bold text-slate-800 mb-1">{isRTL ? "تحلیل نیازها" : "Discovery"}</h5>
                                                <p className="text-[11px] text-slate-500 leading-tight hidden sm:block">{isRTL ? "ارزیابی فنی و مشاوره" : "Requirements analysis"}</p>
                                            </div>
                                            <div className="p-3 rounded-xl bg-blue-50/50 border border-blue-100/60">
                                                <span className="inline-block w-6 h-6 rounded-full bg-[#0072CE] text-white text-xs font-bold leading-6 mb-1">2</span>
                                                <h5 className="text-xs font-bold text-slate-800 mb-1">{isRTL ? "استقرار و تنظیم" : "Deployment"}</h5>
                                                <p className="text-[11px] text-slate-500 leading-tight hidden sm:block">{isRTL ? "سفارشی‌سازی سیستم" : "System setup"}</p>
                                            </div>
                                            <div className="p-3 rounded-xl bg-blue-50/50 border border-blue-100/60">
                                                <span className="inline-block w-6 h-6 rounded-full bg-[#0072CE] text-white text-xs font-bold leading-6 mb-1">3</span>
                                                <h5 className="text-xs font-bold text-slate-800 mb-1">{isRTL ? "پشتیبانی" : "Support"}</h5>
                                                <p className="text-[11px] text-slate-500 leading-tight hidden sm:block">{isRTL ? "آموزش و نگهداری" : "Training & care"}</p>
                                            </div>
                                        </div>
                                    </div>

                                    {/* Section 4: Enterprise Value */}
                                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 mb-6">
                                        <div className="p-3.5 rounded-2xl bg-slate-50 border border-slate-100 flex items-center gap-3">
                                            <div className="w-9 h-9 rounded-xl bg-blue-100 text-[#0072CE] flex items-center justify-center shrink-0">
                                                <FiTrendingUp className="w-4 h-4" />
                                            </div>
                                            <div>
                                                <h5 className="text-xs font-bold text-slate-800">{isRTL ? "ارزش افزوده سازمانی" : "Business ROI"}</h5>
                                                <p className="text-[11px] text-slate-500 mt-0.5">{isRTL ? "ارتقای کارایی و بهینه‌سازی هزینه‌ها" : "Enhanced efficiency & cost optimization"}</p>
                                            </div>
                                        </div>
                                        <div className="p-3.5 rounded-2xl bg-slate-50 border border-slate-100 flex items-center gap-3">
                                            <div className="w-9 h-9 rounded-xl bg-blue-100 text-[#0072CE] flex items-center justify-center shrink-0">
                                                <FiShield className="w-4 h-4" />
                                            </div>
                                            <div>
                                                <h5 className="text-xs font-bold text-slate-800">{isRTL ? "تضمین کیفیت و امنیت" : "Security & Quality"}</h5>
                                                <p className="text-[11px] text-slate-500 mt-0.5">{isRTL ? "پشتیبانی اختصاصی و پایداری شبکه" : "Dedicated support & reliability"}</p>
                                            </div>
                                        </div>
                                    </div>

                                    {/* Action Buttons */}
                                    <div className="flex flex-col sm:flex-row gap-3">
                                        <motion.button
                                            className="flex-1 bg-gradient-to-r from-[#0038A8] via-[#0072CE] to-[#155DFC] text-white py-3.5 px-6 rounded-xl font-semibold text-sm shadow-md shadow-blue-500/20 hover:shadow-lg hover:shadow-blue-500/30 transition-all text-center flex items-center justify-center gap-2"
                                            whileHover={{ scale: 1.01 }}
                                            whileTap={{ scale: 0.99 }}
                                            onClick={scrollToContact}
                                        >
                                            <span>{t("labels.requestConsultation", { ns: "BusinessSolutions" })}</span>
                                            {isRTL ? <FiArrowLeft className="w-4 h-4" /> : <FiArrowRight className="w-4 h-4" />}
                                        </motion.button>
                                        <button
                                            className="px-5 py-3.5 rounded-xl border border-slate-200 text-slate-600 hover:bg-slate-50 text-sm font-semibold transition-colors"
                                            onClick={closeDetails}
                                        >
                                            {t("labels.close", { ns: "BusinessSolutions" })}
                                        </button>
                                    </div>
                                </div>
                            </motion.div>
                        </motion.div>
                    )}
                </AnimatePresence>
            </div>
        </section>
    );
};

export default BusinessSolutions;
